
import { useEffect, useState, useCallback } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "./AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingForm } from "./OnboardingForm";
import { User, LogOut, MessageSquare, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Profile } from "@/types/profile";
import { useTranslation } from 'react-i18next';
import { TFunction } from "i18next";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import i18n from "@/lib/i18n";

// Updated sign out function to handle potential session issues
export const handleSignOut = async (t: TFunction<"translation", undefined>) => {
  try {
    // Try signing out without checking the session
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Error signing out:", error);
      toast.error(t('error_signing_out', { reason: error.message }));
    } else {
      // Force navigation to home page after successful sign out
      toast.success(t('signed_out'));
      // Use window.location to ensure a complete page reload and session clear
      window.location.href = "/";
    }
  } catch (error) {
    console.error("Error signing out:", error);
    toast.error(t('failed_sign_out'));
    // Force navigation to home page even if there's an error
    window.location.href = "/";
  }
};

export const ProfileMenu = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackContent, setFeedbackContent] = useState("");
  const [profile, setProfile] = useState<Profile | null>(null);
  const { t, i18n: i18nInstance } = useTranslation();
  const [language, setLanguage] = useState(i18nInstance.language || 'en');

  // Fetch profile data when component mounts
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
        return;
      }

      // Cast the data to match our Profile type
      const profileData = data as unknown as Profile;
      setProfile(profileData);
    };

    fetchProfile();
  }, [user, showOnboarding]); // Re-fetch when showOnboarding changes (form closes)

  // Handle onboarding dialog close with proper cleanup
  const handleOnboardingClose = useCallback(() => {
    console.log("Closing onboarding dialog");
    setShowOnboarding(false);
    // Small delay to ensure dialog is properly closed before any focus management
    setTimeout(() => {
      // Re-enable pointer events on body if they were disabled
      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
    }, 100);
  }, []);

  // Handle feedback dialog close with proper cleanup
  const handleFeedbackClose = useCallback(() => {
    console.log("Closing feedback dialog");
    setShowFeedback(false);
    setFeedbackContent("");
    // Small delay to ensure dialog is properly closed
    setTimeout(() => {
      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
    }, 100);
  }, []);

  const handleSubmitFeedback = async () => {
    if (!user) {
      toast.error(t('must_be_logged_in_feedback'));
      return;
    }

    if (!feedbackContent.trim()) {
      toast.error(t('enter_feedback_before_submitting'));
      return;
    }

    try {
      const { error } = await supabase.from("user_feedback").insert([
        {
          user_id: user.id,
          content: feedbackContent.trim(),
          page_url: window.location.pathname,
        },
      ]);

      if (error) throw error;

      toast.success(t('thank_you_feedback'));
      handleFeedbackClose();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error(t('failed_submit_feedback'));
    }
  };

  if (!user) return null;

  const displayName = profile?.full_name || user.email?.split('@')[0] || 'User';
  const profileCompleted = profile?.onboarding_completed || false;

  return (
    <div className="flex items-center gap-3">
      <Select
        value={language}
        onValueChange={(lang) => {
          setLanguage(lang);
          i18n.changeLanguage(lang);
        }}
      >
        <SelectTrigger className="w-28 h-8 text-xs mr-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="en">English</SelectItem>
          <SelectItem value="es">Español</SelectItem>
        </SelectContent>
      </Select>
      <span className="text-sm font-medium hidden md:inline-block">
        {t('hello_user', { name: displayName })}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full">
            <Avatar className="h-10 w-10">
              <AvatarImage src={profile?.avatar_url || undefined} />
              <AvatarFallback>
                {profile?.full_name?.charAt(0) || user.email?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {profile?.full_name || t('complete_your_profile')}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowOnboarding(true)}>
            <User className="mr-2 h-4 w-4" />
            <span>{profileCompleted ? t('edit_profile') : t('complete_profile')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/account")}>
            <CreditCard className="mr-2 h-4 w-4" />
            <span>{t('account_and_subscription')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowFeedback(true)}>
            <MessageSquare className="mr-2 h-4 w-4" />
            <span>{t('feedback')}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleSignOut(t)}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t('sign_out')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showOnboarding} onOpenChange={handleOnboardingClose}>
        <DialogContent className="sm:max-w-[550px]" onPointerDownOutside={(e) => e.preventDefault()}>
          <OnboardingForm 
            onComplete={handleOnboardingClose} 
            isProfileComplete={profileCompleted}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showFeedback} onOpenChange={handleFeedbackClose}>
        <DialogContent className="sm:max-w-[425px]" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t('send_feedback')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder={t('feedback_placeholder')}
              value={feedbackContent}
              onChange={(e) => setFeedbackContent(e.target.value)}
              className="min-h-[100px]"
            />
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleFeedbackClose}>
                  {t('cancel')}
                </Button>
                <Button variant="outline" onClick={() => setFeedbackContent("")}>
                  {t('clear')}
                </Button>
              </div>
              <Button onClick={handleSubmitFeedback} className="bg-emerald-500 hover:bg-emerald-600">
                {t('send_feedback')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
